'use client';

import { useEffect, useState } from 'react';
import NavBar from '../home-page/components/NavBar';
import FooterSection from '../home-page/components/FooterSection';
import { generateBreadcrumbSchema } from '@/lib/utils/metadata';
import { legalPageService } from '@/lib/services/legalPageService';

const SITE_URL = process.env.NEXT_PUBLIC_SITE_URL || '';

const DEFAULT_CONTENT = `
<h2>1. Acceptance of Terms</h2>
<p>By accessing and using the GoGedit platform, you accept and agree to be bound by the terms and provisions of this agreement. If you do not agree to these Terms and Conditions, please do not use our services.</p>

<h2>2. Description of Service</h2>
<p>GoGedit provides an on-demand logistics platform that connects customers who need to transport items with drivers who have vehicles available for such transportation. We facilitate the connection but are not responsible for the actual transportation services provided by independent drivers.</p>

<h2>3. User Accounts</h2>
<p>To use our services, you must create an account and provide accurate, complete information. You are responsible for maintaining the confidentiality of your account credentials and for all activities that occur under your account.</p>

<h2>4. User Responsibilities</h2>
<p>Users agree to:</p>
<ul>
<li>Provide accurate information about items to be transported</li>
<li>Ensure items are properly packaged and ready for pickup</li>
<li>Be available at agreed pickup and delivery times</li>
<li>Treat drivers with respect and professionalism</li>
<li>Pay all fees associated with the service</li>
</ul>

<h2>5. Driver Responsibilities</h2>
<p>Drivers agree to:</p>
<ul>
<li>Maintain a valid driver's license and insurance</li>
<li>Keep vehicles in good working condition</li>
<li>Handle items with care during transportation</li>
<li>Communicate professionally with customers</li>
<li>Follow all applicable traffic laws and regulations</li>
</ul>

<h2>6. Prohibited Items</h2>
<p>The following items may not be transported using our platform:</p>
<ul>
<li>Illegal substances or contraband</li>
<li>Hazardous materials</li>
<li>Live animals (except service animals with prior arrangement)</li>
<li>Perishable food items without proper packaging</li>
<li>Items exceeding weight or size limitations</li>
</ul>

<h2>7. Pricing and Payment</h2>
<p>Prices are determined based on distance, vehicle size, and demand. All prices are displayed upfront before booking confirmation. Payment is processed through our secure platform, and users agree to pay all applicable fees.</p>

<h2>8. Cancellation Policy</h2>
<p>Cancellations made more than 2 hours before scheduled pickup receive a full refund. Cancellations within 2 hours may incur a cancellation fee. Drivers who cancel confirmed bookings may face account penalties.</p>

<h2>9. Liability and Insurance</h2>
<p>All deliveries are covered by our insurance policy up to the declared value. Users must accurately declare item values. GoGedit is not liable for damages resulting from improper packaging, prohibited items, or acts beyond our control.</p>

<h2>10. Dispute Resolution</h2>
<p>Any disputes arising from use of our services will be resolved through binding arbitration in accordance with the laws of Ontario, Canada.</p>

<h2>11. Changes to Terms</h2>
<p>GoGedit reserves the right to modify these terms at any time. Users will be notified of significant changes, and continued use of the platform constitutes acceptance of modified terms.</p>

<h2>12. Contact Information</h2>
<p>For questions about these Terms and Conditions, please contact us at:<br />
<strong>Email:</strong> info@gogedit.ca<br />
<strong>Location:</strong> Toronto, Ontario, Canada</p>
`;

export default function TermsAndConditionsPage() {
  const [content, setContent] = useState<string>('');
  const [title, setTitle] = useState<string>('Terms and Conditions');
  const [updatedAt, setUpdatedAt] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const breadcrumbSchema = generateBreadcrumbSchema([
    { name: 'Home', url: `${SITE_URL}/home-page` },
    { name: 'Terms and Conditions', url: `${SITE_URL}/terms-and-conditions` },
  ]);

  useEffect(() => {
    async function fetchContent() {
      try {
        const page = await legalPageService?.getBySlug('terms-conditions');
        if (page && page?.content) {
          setContent(page?.content);
          setTitle(page?.title);
          setUpdatedAt(page?.updatedAt);
        } else {
          setContent(DEFAULT_CONTENT);
        }
      } catch {
        setContent(DEFAULT_CONTENT);
      } finally {
        setLoading(false);
      }
    }
    fetchContent();
  }, []);

  const formattedDate = updatedAt
    ? new Date(updatedAt)?.toLocaleDateString('en-CA', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      })
    : 'March 10, 2026';

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }}
      />
      <div className="min-h-screen bg-white">
        <NavBar />

        <div className="pt-24 pb-16">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h1 className="text-5xl font-bold text-navy-dark mb-4">{title}</h1>

            <p className="text-body-text mb-8 text-sm">
              Last updated: {formattedDate}
            </p>

            {loading ? (
              <div className="flex items-center justify-center py-16">
                <div className="w-10 h-10 border-4 border-brand-purple border-t-transparent rounded-full animate-spin"></div>
              </div>
            ) : (
              <div
                className="prose prose-lg max-w-none prose-headings:text-navy-dark prose-headings:font-bold prose-h2:text-2xl prose-h2:mt-8 prose-h2:mb-4 prose-p:text-body-text prose-p:mb-4 prose-li:text-body-text prose-ul:pl-6 prose-ul:space-y-2 prose-ul:mb-4 prose-strong:text-navy-dark"
                dangerouslySetInnerHTML={{ __html: content }}
              />
            )}
          </div>
        </div>

        <FooterSection />
      </div>
    </>
  );
}